'use client';

import { ReactNode, useEffect, useState } from 'react';
import { useSupabaseAuth } from './SupabaseAuthContext';
import { usePathname } from 'next/navigation';

// Paths that render WITHOUT waiting for the client auth session.
//
// CRITICAL — THIS LIST IS AN SEO SURFACE, NOT JUST A UX DETAIL.
// AuthLoader is a client component in the root layout chain. For any path NOT
// listed here it returns a spinner INSTEAD of {children}, and that also happens
// during server rendering — so the page's server components never reach the
// HTML shell. Measured on production 2026-08-08, before this list was fixed:
//
//   /               14,038 chars of visible text   (was listed → fine)
//   /pricing            62 chars  "Initializing authentication..."
//   /for-teams          82 chars  "Initializing authentication..."
//   /for-dev-teams      58 chars  "Initializing authentication..."
//   /about              61 chars  "Initializing authentication..."
//   /studio             66 chars  "Initializing authentication..."
//
// Every marketing page except the homepage was serving an empty shell to
// crawlers that do not execute JavaScript — which is most AI crawlers and every
// link-preview bot. The pages could never rank because there was no body copy
// to rank, no matter how good the metadata was.
//
// RULE: any page that must be readable by a logged-OUT visitor or a crawler
// belongs here. Keep it in sync with app/sitemap.ts.
const PUBLIC_PATHS = [
  // App shell / auth
  '/',
  '/login',
  '/home',
  '/fallback',
  // Marketing — every entry here is also in app/sitemap.ts
  '/start', // booking-page product entry point
  '/pricing',
  '/for-teams',
  '/for-dev-teams',
  '/studio',
  '/about',
  '/ai-transparency',
  '/privacy',
  '/terms',
  '/routines/new',
  '/es',
  '/fr',
];

// Public path PREFIXES — for route families whose full path is dynamic but
// which are still public (no auth required to read them).
const PUBLIC_PATH_PREFIXES = [
  '/api/',
  '/webhooks/',
  '/home/', // /home/guides and friends
  '/routines/new/', // template picker deep links: /routines/new/[slug]
  '/claim/', // hero-call claim pages (anonymous by definition)
  '/for/', // industry landing pages: /for/hotels, /for/construction, …
  '/es/', // Spanish marketing pages
  '/fr/', // French marketing pages
];

// How long auth init may take before we surface the recovery screen. The auth
// client now bounds its lock acquisition and initializeAuth races a defensive
// getSession timeout (see project_auth_init_stall), so a healthy-but-slow mobile
// init resolves well inside this window as a spinner — the recovery screen is
// reserved for genuinely stuck states.
const AUTH_FALLBACK_TIMEOUT_MS = 12000;

interface AuthLoaderProps {
  children: ReactNode;
}

/**
 * AuthLoader component with timeout circuit breaker
 * Provides loading states and handles auth initialization
 */
export default function AuthLoader({ children }: AuthLoaderProps) {
  const { status, isLoading, error } = useSupabaseAuth();
  const [showFallback, setShowFallback] = useState(false);
  const pathname = usePathname();
  
  // Determine if the current path is public
  const isPublicPath =
    PUBLIC_PATHS.includes(pathname || '') ||
    PUBLIC_PATH_PREFIXES.some((prefix) => pathname?.startsWith(prefix)) ||
    pathname?.includes('/webhooks/');
  
  // Set a timeout for showing fallback UI
  useEffect(() => {
    // Don't show fallback for public routes
    if (isPublicPath) return;

    // If auth has already resolved (either way), clear any latched fallback and
    // stop here. Without this reset the fallback was sticky: once shown it never
    // went away even after auth completed a moment later (the mobile dead-end).
    if (!isLoading && status !== 'loading') {
      setShowFallback(false);
      return;
    }

    // Still initializing — arm the recovery screen if it drags on.
    const timeout = setTimeout(() => {
      if (isLoading || status === 'loading') {
        setShowFallback(true);
      }
    }, AUTH_FALLBACK_TIMEOUT_MS);

    // Clear timeout when component unmounts or auth state changes
    return () => clearTimeout(timeout);
  }, [isLoading, status, isPublicPath]);
  
  // Allow immediate rendering for public paths
  if (isPublicPath) {
    return <>{children}</>;
  }
  
  // Show fallback UI if initialization is taking too long
  if (showFallback) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md w-full p-8 bg-card border rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-foreground">Still getting you in…</h2>
          <p className="text-muted-foreground mb-6">
            This is taking longer than usual. You&apos;re most likely still signed in —
            reloading usually fixes it. If you have this site open in other tabs,
            closing them can help.
          </p>

          <div className="flex flex-col space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="py-2 px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 font-medium"
            >
              Reload Page
            </button>

            <button
              onClick={() => window.location.href = '/login'}
              className="py-2 px-4 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
            >
              Go to Login
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={() => window.location.href = '/api/auth/reset-session?redirectTo=/login'}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Reset session
            </button>
            <p className="mt-1 text-xs text-muted-foreground">
              Last resort — this signs you out, and you&apos;ll need to sign in again.
            </p>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 rounded text-xs text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Show loading UI if still initializing
  if (isLoading || status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center">
          <div className="h-8 w-8 rounded-full border-4 border-primary border-r-transparent animate-spin mb-4"></div>
          <p className="text-muted-foreground">Initializing authentication...</p>
        </div>
      </div>
    );
  }
  
  // Authentication is initialized, render children
  return <>{children}</>;
}